// @greenlight-spec: 28-it-dashboard-ui.md
// Catches a render error inside the routed page so one broken surface degrades
// to a message inside the shell instead of unmounting the dashboard. React has
// no hook form of componentDidCatch, so this stays a class.
import * as React from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface Props {
  children: React.ReactNode;
  /** Remounts the boundary when it changes, so navigating away clears the error. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class RouteErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Alert variant="destructive" data-testid="route-error">
        <AlertTitle>This page hit an error</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>
            Nothing was lost — the rest of the dashboard still works. Reloading usually clears it.
          </p>
          <p className="font-mono text-xs">{error.message}</p>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
}
